import { LicenseStatusBadge, SeatsIndicator, TypeBadge } from './Badges'
import { formatDate } from './helpers'
import type { License, LicenseStatus, LicenseType } from '../../types'

const HEADERS = ['License Key', 'Organisation', 'Type', 'Status', 'Seats', 'Valid Until'] as const

const TABLE_COL_SPAN = 7

interface RowActionsProps {
  license: License
  onEdit: (license: License) => void
  onExtend: (license: License) => void
  onActivate: (license: License) => void
  onSuspend: (license: License) => void
}

const ICON_BUTTON_BASE = 'p-1.5 rounded'

function EditIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  )
}

function ExtendIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  )
}

function ActivateIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

function SuspendIcon(): React.ReactElement {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

function StatusToggleButton({
  license,
  onActivate,
  onSuspend,
}: RowActionsProps): React.ReactElement {
  const isInactive = license.status === 'suspended' || license.status === 'expired'
  if (isInactive) {
    return (
      <button
        onClick={() => {
          onActivate(license)
        }}
        className={`${ICON_BUTTON_BASE} text-green-500 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300`}
        title="Activate"
      >
        <ActivateIcon />
      </button>
    )
  }
  return (
    <button
      onClick={() => {
        onSuspend(license)
      }}
      className={`${ICON_BUTTON_BASE} text-amber-500 hover:bg-amber-50 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20 dark:hover:text-amber-300`}
      title="Suspend"
    >
      <SuspendIcon />
    </button>
  )
}

function RowActions(props: RowActionsProps): React.ReactElement {
  const { license, onEdit, onExtend } = props
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={() => {
          onEdit(license)
        }}
        className={`${ICON_BUTTON_BASE} text-stone-500 hover:bg-stone-100 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700 dark:hover:text-stone-200`}
        title="Edit"
      >
        <EditIcon />
      </button>
      <button
        onClick={() => {
          onExtend(license)
        }}
        className={`${ICON_BUTTON_BASE} text-blue-500 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300`}
        title="Extend"
      >
        <ExtendIcon />
      </button>
      <StatusToggleButton {...props} />
    </div>
  )
}

interface LicenseRowProps {
  license: License
  getOrgName: (orgId: string) => string
  onEdit: (license: License) => void
  onExtend: (license: License) => void
  onActivate: (license: License) => void
  onSuspend: (license: License) => void
}

function LicenseRow(props: LicenseRowProps): React.ReactElement {
  const { license, getOrgName } = props
  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-700/50">
      <td className="px-4 py-3">
        <code className="rounded bg-stone-100 px-2 py-1 text-sm text-stone-900 dark:bg-stone-700 dark:text-stone-100">
          {license.licenseKey}
        </code>
      </td>
      <td className="px-4 py-3 text-sm text-stone-700 dark:text-stone-300">
        {getOrgName(license.organisationId)}
      </td>
      <td className="px-4 py-3">
        <TypeBadge licenseType={license.licenseType as LicenseType} />
      </td>
      <td className="px-4 py-3">
        <LicenseStatusBadge status={license.status as LicenseStatus} isValid={license.isValid} />
      </td>
      <td className="px-4 py-3">
        <SeatsIndicator used={license.seatsUsed} total={license.seatsTotal} />
      </td>
      <td className="px-4 py-3 text-sm text-stone-600 dark:text-stone-400">
        {formatDate(license.validUntil)}
      </td>
      <td className="px-4 py-3">
        <RowActions
          license={license}
          onEdit={props.onEdit}
          onExtend={props.onExtend}
          onActivate={props.onActivate}
          onSuspend={props.onSuspend}
        />
      </td>
    </tr>
  )
}

interface LicensesTableProps {
  licenses: License[]
  getOrgName: (orgId: string) => string
  onEdit: (license: License) => void
  onExtend: (license: License) => void
  onActivate: (license: License) => void
  onSuspend: (license: License) => void
}

function TableHead(): React.ReactElement {
  return (
    <thead className="border-b border-stone-200 bg-stone-50 dark:border-stone-700 dark:bg-stone-800/50">
      <tr>
        {HEADERS.map((header) => (
          <th
            key={header}
            className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400"
          >
            {header}
          </th>
        ))}
        <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          Actions
        </th>
      </tr>
    </thead>
  )
}

export function LicensesTable(props: LicensesTableProps): React.ReactElement {
  const { licenses, getOrgName, onEdit, onExtend, onActivate, onSuspend } = props
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <div className="overflow-x-auto">
        <table className="w-full">
          <TableHead />
          <tbody className="divide-y divide-stone-200 dark:divide-stone-700">
            {licenses.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COL_SPAN}
                  className="px-4 py-8 text-center text-stone-500 dark:text-stone-400"
                >
                  No licenses found
                </td>
              </tr>
            ) : (
              licenses.map((license) => (
                <LicenseRow
                  key={license.id}
                  license={license}
                  getOrgName={getOrgName}
                  onEdit={onEdit}
                  onExtend={onExtend}
                  onActivate={onActivate}
                  onSuspend={onSuspend}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
